/**
 * The page picker (1.J deliverable 9): an in-place combobox over `search.picker`, asked 150 ms
 * after the last keystroke. It lists canon hits, never the page itself, and may end with a
 * "Create <type>" option. It is a layer: Esc closes it and focus goes back to where it came from.
 */
import type { DocHitV1 } from '@codex/contracts';
import { TypeGlyph, useLayer, useReturnFocus } from '@codex/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useState } from 'react';
import { useLinksApi } from '../api.js';
import { pickerKeyTarget } from '../keys.js';
import { glyphFor } from '../story/glyph.js';
import { createLabel, type PickerOption, pickerOptions } from './picker-model.js';

export const PICKER_DEBOUNCE_MS = 150;

export interface DocPickerProps {
  readonly label: string;
  readonly selfId: string;
  /** The type a "Create" option makes; null offers no create option. */
  readonly createType: string | null;
  readonly capability?: 'structural';
  readonly trigger: HTMLElement | null;
  readonly onPick: (hit: DocHitV1) => void;
  readonly onCreate: (typeKey: string, title: string) => void;
  readonly onClose: () => void;
}

export function DocPicker({
  label,
  selfId,
  createType,
  capability,
  trigger,
  onPick,
  onCreate,
  onClose,
}: DocPickerProps) {
  const api = useLinksApi();
  const listId = useId();
  const [text, setText] = useState('');
  const [query, setQuery] = useState('');
  const [rawActive, setActive] = useState(0);
  useLayer(true, onClose, 'picker');
  useReturnFocus(true, trigger);

  useEffect(() => {
    const timer = setTimeout(() => setQuery(text), PICKER_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [text]);

  const result = useQuery({
    queryKey: ['links', 'picker', query, capability ?? null],
    queryFn: ({ signal }) =>
      api.picker({ q: query, limit: 8, ...(capability ? { capability } : {}) }, signal),
    gcTime: 0,
    retry: false,
  });
  // The hits are for what is typed only once the debounce has caught up and the answer is in.
  const settled = result.isSuccess && text === query;
  const options = useMemo(
    () => pickerOptions(result.data?.canon ?? [], selfId, query, createType, settled),
    [result.data, settled, selfId, query, createType],
  );

  const active = Math.min(rawActive, Math.max(options.length - 1, 0));
  const choose = (option: PickerOption | undefined) => {
    if (option === undefined) return;
    if (option.kind === 'doc') onPick(option.hit);
    else onCreate(option.typeKey, option.title);
  };
  const optionId = (index: number) => `${listId}-${index}`;

  return (
    <div className="cx-links-picker" data-testid="links.picker.root">
      <input
        type="text"
        role="combobox"
        aria-label={label}
        aria-expanded={options.length > 0}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={options.length > 0 ? optionId(active) : undefined}
        className="cx-links-picker__input"
        placeholder="Find a page"
        value={text}
        // biome-ignore lint/a11y/noAutofocus: the picker opens from a button and takes focus
        autoFocus
        onChange={(event) => {
          setText(event.target.value);
          setActive(0);
        }}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            choose(options[active]);
            return;
          }
          const target = pickerKeyTarget(
            event.key,
            active,
            options.length,
            event.ctrlKey || event.metaKey,
          );
          if (target === null) return;
          event.preventDefault();
          setActive(target);
        }}
      />
      <div
        id={listId}
        role="listbox"
        aria-label={`${label} results`}
        className="cx-links-picker__list"
      >
        {options.map((option, index) => (
          // biome-ignore lint/a11y/useKeyWithClickEvents: the combobox input owns the keyboard (aria-activedescendant)
          <div
            key={option.id}
            id={optionId(index)}
            role="option"
            tabIndex={-1}
            aria-selected={index === active}
            className="cx-links-picker__option"
            onPointerDown={(event) => event.preventDefault()}
            onClick={() => choose(option)}
          >
            {option.kind === 'doc' ? (
              <>
                <TypeGlyph type={glyphFor(option.hit.typeKey)} />
                <span>{option.hit.title}</span>
              </>
            ) : (
              <span>{createLabel(option.typeKey, option.title)}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
